import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { CreditCard, Save, AlertTriangle, TrendingUp, DollarSign, Mail } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

interface Organization {
  id: string;
  name: string;
}

interface BillingTabProps {
  organization: Organization;
  onSave: (data: any) => void;
}

export function BillingTab({ organization, onSave }: BillingTabProps) {
  const [balance] = useState(1250.75) // SEK
  const [billing, setBilling] = useState({
    auto_renew: false,
    threshold: 100,
    purchase_amount: 500,
    payment_method: 'card_ending_4321'
  })

  const [budgetAlert, setBudgetAlert] = useState({
    enabled: true,
    monthly_limit: 2000,
    recipient_email: 'admin@example.com'
  })

  const [usageData, setUsageData] = useState<Array<{date: string, amount: number}>>([])
  
  useEffect(() => {
    fetchUsageData()
  }, [])

  const fetchUsageData = async () => {
    try {
      const { data, error } = await supabase
        .from('billing_transactions')
        .select('created_at, credits')
        .order('created_at', { ascending: true })
        .limit(30)
      
      if (error) throw error
      
      const formattedData = (data || []).map(transaction => ({
        date: new Date(transaction.created_at).toISOString().split('T')[0],
        amount: Math.abs(transaction.credits || 0)
      }))
      
      setUsageData(formattedData)
    } catch (error) {
      console.error('Error fetching usage data:', error)
    }
  }

  const handleSave = () => {
    onSave({ billing, budgetAlert })
  }

  const updatePaymentMethod = () => {
    // TODO: Integrate with Stripe
    console.log('Redirect to Stripe payment method update')
  }

  const currentMonthUsage = 875.50 // SEK

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Kontosaldo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-2xl font-bold">{balance.toLocaleString('sv-SE')} SEK</p>
              <p className="text-sm text-muted-foreground">Aktuellt saldo</p>
            </div>
            <Badge variant={balance < billing.threshold ? "destructive" : "default"} className="px-3 py-1">
              {balance < billing.threshold ? "Lågt saldo" : "Normalt"}
            </Badge>
          </div>

          {balance < billing.threshold && (
            <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <span className="text-sm text-orange-800">
                Saldot är under tröskelvärdet på {billing.threshold} SEK
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Automatisk påfyllning
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Auto-förnyelse</Label>
              <p className="text-sm text-muted-foreground">Fyll på saldot automatiskt när det når tröskelvärdet</p>
            </div>
            <Switch
              checked={billing.auto_renew}
              onCheckedChange={(checked) => setBilling(prev => ({ ...prev, auto_renew: checked }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="threshold">Tröskel (SEK)</Label>
              <Input
                id="threshold"
                type="number"
                value={billing.threshold}
                onChange={(e) => setBilling(prev => ({ ...prev, threshold: parseInt(e.target.value) }))}
                min="50"
                max="1000"
              />
              <p className="text-xs text-muted-foreground mt-1">Påfyllning sker när saldot understiger denna summa</p>
            </div>
            
            <div>
              <Label htmlFor="purchase_amount">Påfyllningsbelopp (SEK)</Label>
              <Input
                id="purchase_amount"
                type="number"
                value={billing.purchase_amount}
                onChange={(e) => setBilling(prev => ({ ...prev, purchase_amount: parseInt(e.target.value) }))}
                min="100"
                max="5000"
                step="100"
              />
              <p className="text-xs text-muted-foreground mt-1">Belopp som läggs till vid påfyllning</p>
            </div>
          </div>

          <div>
            <Label>Betalmetod</Label>
            <div className="flex items-center justify-between p-3 border rounded-lg mt-1">
              <div className="flex items-center gap-3">
                <div className="w-8 h-5 bg-blue-600 rounded flex items-center justify-center">
                  <span className="text-xs text-white font-bold">VISA</span>
                </div>
                <span className="text-sm">**** **** **** 4321</span>
              </div>
              <Button variant="outline" size="sm" onClick={updatePaymentMethod}>
                Ändra
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Budgetalarm
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Månadsbudget</Label>
              <p className="text-sm text-muted-foreground">Få varning när månatlig användning överstiger gränsen</p>
            </div>
            <Switch
              checked={budgetAlert.enabled}
              onCheckedChange={(checked) => setBudgetAlert(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {budgetAlert.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="monthly_limit">Månadsgräns (SEK)</Label>
                <Input
                  id="monthly_limit"
                  type="number"
                  value={budgetAlert.monthly_limit}
                  onChange={(e) => setBudgetAlert(prev => ({ ...prev, monthly_limit: parseInt(e.target.value) }))}
                  min="100"
                  max="10000"
                  step="100"
                />
              </div>
              
              <div>
                <Label htmlFor="recipient_email">E-postmottagare</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={budgetAlert.recipient_email}
                  onChange={(e) => setBudgetAlert(prev => ({ ...prev, recipient_email: e.target.value }))}
                  placeholder="admin@example.com"
                />
              </div>
            </div>
          )}

          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Användning denna månad:</span>
              <span className="text-sm font-bold">{currentMonthUsage.toLocaleString('sv-SE')} SEK</span>
            </div>
            <div className="w-full bg-white rounded-full h-2 mt-2">
              <div 
                className="bg-blue-600 h-2 rounded-full" 
                style={{ width: `${Math.min((currentMonthUsage / budgetAlert.monthly_limit) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((currentMonthUsage / budgetAlert.monthly_limit) * 100).toFixed(1)}% av månadsbudget
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Användningshistorik (senaste 15 dagarna)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={usageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' })}
                />
                <YAxis tickFormatter={(value) => `${value} SEK`} />
                <Tooltip 
                  labelFormatter={(label) => new Date(label).toLocaleDateString('sv-SE')}
                  formatter={(value) => [`${value} SEK`, 'Användning']}
                />
                <Line type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Save className="h-4 w-4" />
          Spara billinginställningar
        </Button>
      </div>
    </div>
  )
}